import { InvoiceAuditLineFragment, WasteContractRateRule } from "generated-graphql/graphql";

import { formatInvoiceDate } from "../utils";
import { formatAuditMoney, formatUnitRate } from "./formatAuditValues";

export type ContractRate = NonNullable<InvoiceAuditLineFragment["matchedRate"]>;
export const CONTRACT_RULE_LABELS = {
  PER_UNIT: "Per unit",
  MINIMUM: "Minimum charge",
  FLAT: "Flat fee",
  BASE_PLUS_ADDITIONAL: "Base charge + additional units",
  CASE_BY_CASE: "Case by case",
  QUOTE: "Quote",
  DOCUMENTED_COST: "Documented cost",
} satisfies Record<WasteContractRateRule, string>;

export type ContractRateField = {
  key: keyof ContractRate;
  label: string;
} & (
  | { kind: "rule"; value: WasteContractRateRule }
  | { kind: "text" | "money" | "id"; value: string | null }
  | { kind: "unitRate" | "quantity"; value: string | null; unit: string | null }
  | { kind: "period"; value: string; end: string | null }
);

type PricingFieldSpec = {
  key:
    | "rateAmount"
    | "minimumAmount"
    | "additionalRateAmount"
    | "quantityIncludedInBaseRate"
    | "billingUnit";
  label: string;
  kind: "money" | "unitRate" | "quantity" | "text";
  optional?: boolean;
};
const UNIT_RATE: PricingFieldSpec = { key: "rateAmount", label: "Unit rate", kind: "unitRate" };
const BILLING_UNIT: PricingFieldSpec = { key: "billingUnit", label: "Billing unit", kind: "text" };
const RECORDED_AMOUNT: PricingFieldSpec = {
  key: "rateAmount",
  label: "Recorded contract amount",
  kind: "money",
  optional: true,
};

const PRICING_FIELDS: Readonly<Record<WasteContractRateRule, readonly PricingFieldSpec[]>> = {
  PER_UNIT: [UNIT_RATE, BILLING_UNIT],
  MINIMUM: [
    UNIT_RATE,
    { key: "minimumAmount", label: "Contract minimum", kind: "money" },
    BILLING_UNIT,
  ],
  FLAT: [{ key: "rateAmount", label: "Flat amount", kind: "money" }],
  BASE_PLUS_ADDITIONAL: [
    { key: "rateAmount", label: "Base charge", kind: "money" },
    { key: "additionalRateAmount", label: "Additional unit rate", kind: "unitRate" },
    { key: "quantityIncludedInBaseRate", label: "Included quantity", kind: "quantity" },
    BILLING_UNIT,
  ],
  QUOTE: [RECORDED_AMOUNT],
  CASE_BY_CASE: [RECORDED_AMOUNT],
  DOCUMENTED_COST: [RECORDED_AMOUNT],
} satisfies Record<WasteContractRateRule, readonly PricingFieldSpec[]>;

/** Select relevant contract fields without formatting values or changing matching rules. */
export function getContractRateFields(rate: ContractRate): ContractRateField[] {
  const fields: ContractRateField[] = [
    { key: "description", label: "Description", kind: "text", value: rate.description },
    { key: "rule", label: "Rule", kind: "rule", value: rate.rule },
  ];
  const unit = rate.billingUnit?.trim() || null;
  for (const spec of PRICING_FIELDS[rate.rule]) {
    const value = rate[spec.key]?.trim() || null;
    if (spec.optional && value == null) continue;
    fields.push({ key: spec.key, label: spec.label, kind: spec.kind, value, unit });
  }
  fields.push({
    key: "effectiveFrom",
    label: "Effective period",
    kind: "period",
    value: rate.effectiveFrom,
    end: rate.effectiveThrough ?? null,
  });
  if (unit && !fields.some(({ key }) => key === "billingUnit")) {
    fields.push({ ...BILLING_UNIT, kind: "text", value: unit });
  }
  for (const { key, label, value } of [
    {
      key: "wasteCodes",
      label: "Waste codes",
      value: rate.wasteCodes.filter((code) => code.trim()).join(", "),
    },
    {
      key: "pricingKeys",
      label: "Pricing keys",
      value: rate.pricingKeys.filter((key) => key.trim()).join(", "),
    },
    { key: "generatorJurisdiction", label: "Jurisdiction", value: rate.generatorJurisdiction },
    { key: "scheduleType", label: "Schedule", value: rate.scheduleType },
    { key: "pricingRole", label: "Pricing role", value: rate.pricingRole },
  ] satisfies { key: keyof ContractRate; label: string; value: string | null | undefined }[]) {
    if (value?.trim()) fields.push({ key, label, kind: "text", value });
  }
  fields.push({ key: "id", label: "Rate ID", kind: "id", value: rate.id });
  return fields;
}

export function formatContractRateField(field: ContractRateField): string {
  if (field.value == null || !field.value.trim()) return "Not provided";
  switch (field.kind) {
    case "money":
      return field.key === "minimumAmount"
        ? formatAuditMoney("minimumAmount", field.value)
        : formatAuditMoney("rateAmount", field.value);
    case "unitRate":
      return field.unit
        ? formatUnitRate(field.value, field.unit)
        : formatAuditMoney("rateAmount", field.value);
    case "quantity":
      return [field.value, field.unit].filter(Boolean).join(" ");
    case "period":
      return `${formatInvoiceDate(field.value)} through ${field.end ? formatInvoiceDate(field.end) : "open ended"}`;
    case "text":
      return field.key === "pricingRole" || field.key === "scheduleType"
        ? field.value
            .toLowerCase()
            .replaceAll("_", " ")
            .replace(/^./, (char) => char.toUpperCase())
        : field.value;
    default:
      return field.value;
  }
}
